import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Home, ArrowLeft, ArrowRight, Mail, MapPin, Unlock, Lock } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RetrieveInsight() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [insights, setInsights] = useState(null);

  const handleRetrieve = async () => {
    if (!email || !email.includes('@')) {
      toast.error("Please enter a valid email");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/cipher/retrieve`, { email });
      setInsights(res.data.insights);
      if (res.data.insights.length === 0) {
        toast.info("No saved insights found for this email");
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.info("No saved insights found for this email");
        setInsights([]);
      } else {
        toast.error("Failed to retrieve insights");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-cipher-bone flex flex-col">
      {/* Header */}
      <header className="cipher-container pt-6 pb-4">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2" data-testid="logo">
            <div className="w-8 h-8 rounded-lg bg-cipher-green flex items-center justify-center">
              <Home className="w-4 h-4 text-white" />
            </div>
            <span className="font-heading font-bold text-lg text-cipher-charcoal">HiPPO</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 cipher-container py-8">
        <button onClick={() => navigate('/')} className="flex items-center gap-1 text-gray-500 hover:text-cipher-green mb-6" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back to home</span>
        </button>

        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-cipher-green/10 flex items-center justify-center mx-auto mb-6">
            <Mail className="w-8 h-8 text-cipher-green" strokeWidth={1.5} />
          </div>
          <h1 className="font-poppins font-semibold text-3xl text-cipher-charcoal mb-3">
            My Saved Insights
          </h1>
          <p className="text-gray-500">Enter your email to retrieve your saved HiPPO insights</p>
        </div>

        {!insights ? (
          <div className="max-w-xs mx-auto">
            <Input
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="cipher-input text-center"
              onKeyDown={(e) => e.key === 'Enter' && handleRetrieve()}
              data-testid="email-input"
            />
            
            <Button 
              onClick={handleRetrieve}
              className="cipher-btn-primary w-full mt-6"
              disabled={loading}
              data-testid="retrieve-btn"
            >
              {loading ? (
                <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  Find My Insights
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        ) : insights.length === 0 ? (
          <div className="text-center">
            <p className="text-gray-500 mb-6">No saved insights found for {email}</p>
            <Button onClick={() => navigate('/')} className="cipher-btn-primary">
              Start New Assessment
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-gray-500 mb-4">Found {insights.length} saved insight{insights.length > 1 ? 's' : ''}</p>
            
            {insights.map((insight, idx) => (
              <Card 
                key={idx} 
                className="p-5 cursor-pointer hover:border-cipher-green/50 transition-all"
                onClick={() => navigate(`/insight/${insight.session_id}`)}
                data-testid={`insight-${idx}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-cipher-green/10 flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-cipher-green" />
                    </div>
                    <div>
                      <p className="font-semibold text-cipher-charcoal">{insight.postcode}</p>
                      <p className="text-sm text-gray-500">{insight.first_name}'s property</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {insight.is_unlocked ? (
                      <span className="cipher-badge cipher-badge-high">
                        <Unlock className="w-3 h-3 mr-1" />Unlocked
                      </span>
                    ) : (
                      <span className="cipher-badge bg-gray-100 text-gray-600 border-gray-200">
                        <Lock className="w-3 h-3 mr-1" />Teaser
                      </span>
                    )}
                    <ArrowRight className="w-5 h-5 text-gray-300" />
                  </div>
                </div>
                {insight.saved_at && (
                  <p className="text-xs text-gray-400 mt-3">
                    Saved {new Date(insight.saved_at).toLocaleDateString('en-GB')}
                  </p>
                )}
              </Card>
            ))}
            
            <div className="pt-6 text-center">
              <Button onClick={() => navigate('/')} variant="outline" className="cipher-btn-secondary">
                Start New Assessment
              </Button>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="cipher-container py-6">
        <p className="text-center text-xs text-gray-400">
          HiPPO by StudioNPG — Signals, not promises
        </p>
      </footer>
    </div>
  );
}
